import * as React from 'react';
import * as Slot from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { cn } from './cn';
import { focusGlow } from './focus';

const buttonVariants = cva(
  [
    // base
    'inline-flex items-center justify-center whitespace-nowrap rounded-sm bg-transparent text-sm font-medium transition-colors',
    // disabled
    'disabled:pointer-events-none disabled:opacity-50',
    // focus
    focusGlow,
  ],
  {
    variants: {
      variant: {
        primary:
          'border border-primary bg-primary font-semibold text-white shadow-xs hover:bg-blue-400',
        primaryOutline:
          'border border-primary font-semibold text-primary shadow-xs hover:text-blue-400',
        primaryTag:
          'border border-blue-600 bg-primary-transparent font-semibold text-blue-600 shadow-xs hover:text-blue-500 dark:border-blue-500 dark:text-blue-500 dark:hover:text-blue-400',
        GreenTag:
          'border border-green-600 bg-green-100/90 font-semibold text-green-600 shadow-xs hover:text-green-500 dark:border-green-500 dark:bg-green-800/90 dark:text-green-500 dark:hover:text-green-400',
        outline:
          'border border-border font-semibold text-black shadow-xs hover:border-primary hover:text-primary dark:text-white bg-input-transparent',
        link: 'text-primary hover:scale-110 hover:text-blue-400',
        text: 'hover:scale-110 hover:text-primary',
        nav: 'hover:border hover:border-primary hover:text-primary',
        ghost:
          'hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-gray-50',
        destructive:
          'bg-destructive border-destructive font-semibold text-destructive-foreground shadow-xs hover:bg-destructive/90',
        destructiveOutline:
          'border border-destructive/40 font-semibold text-destructive/90 shadow-xs hover:text-destructive-foreground',
        warning:
          'bg-warning border border-warning font-semibold text-white shadow-xs hover:bg-warning/90',
        select:
          'flex h-9 w-full items-center justify-between whitespace-nowrap border border-border bg-transparent px-3 py-2 shadow-xs ring-offset-background placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
      },
      size: {
        md: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 py-2',
        lg: 'h-10 px-5 py-3',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

const iconVariants = cva('', {
  variants: {
    size: {
      md: 'size-4',
      sm: 'size-4',
      lg: 'size-5',
      icon: 'size-5',
    },
    position: {
      left: 'mr-1.5',
      right: 'ml-1.5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  ref?: React.Ref<HTMLButtonElement>;
  asChild?: boolean;
  loading?: boolean;
  iconLeft?: React.ReactElement<{ className?: string }>;
  iconRight?: React.ReactElement<{ className?: string }>;
  testId?: string;
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      disabled,
      loading,
      iconLeft,
      iconRight,
      asChild = false,
      children,
      testId = 'cd-button',
      onClick,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    const Comp = asChild ? Slot.Root : 'button';
    const content = asChild ? (
      children
    ) : (
      <>
        {!loading &&
          iconLeft &&
          React.isValidElement(iconLeft) &&
          React.cloneElement(iconLeft, {
            className: cn(
              iconVariants({
                size,
                position: 'left',
                class: iconLeft.props.className,
              }),
            ),
          })}
        {children}
        {!loading &&
          iconRight &&
          React.isValidElement(iconRight) &&
          React.cloneElement(iconRight, {
            className: cn(
              iconVariants({
                size,
                position: 'right',
                class: iconRight.props.className,
              }),
            ),
          })}
        {loading ? (
          <CircleNotchIcon className={cn('ml-1.5 size-5 animate-spin')} />
        ) : null}
      </>
    );

    return (
      <Comp
        ref={ref}
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(buttonVariants({ variant, size, className }))}
        data-testid={testId}
        disabled={isDisabled}
        onClick={onClick}
        {...props}
      >
        {content}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
